import * as React from "react"
import { Interval, type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import dateTimeHelper, { type TimeRange } from "@digits-shared/helpers/dateTimeHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { useDateRangeContext } from "src/shared/components/DateRangeSelector/DateRangeContext"
/*
  STYLES
*/

const PresetsContainer = styled.div`
  .desktop & {
    min-width: 160px;
    padding: 16px;
  }
  .mobile & {
    padding: 14px;
  }
`

const Presets = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PresetButton = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background: ${colors.translucentSecondary05};
  }

  & > div {
    flex: 1;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`

const Divider = styled.div`
  margin: 8px 0;
  background: rgba(50, 73, 127, 0.1);
  width: 100%;
  height: 1px;
`

/*
  INTERFACES
*/

interface PresetsProps {
  onChange?: (timeRange: TimeRange) => void
}

interface PresetProps {
  origin: IntervalOrigin
  active?: boolean
  onSelect: (origin: IntervalOrigin) => void
  children?: React.ReactNode
}

/*
  COMPONENTS
*/

export const DateRangePresets: React.FC<PresetsProps> = ({ onChange }) => {
  const todayOrigin = dateTimeHelper.todayIntervalOrigin(Interval.Month)
  const quarterOrigin = dateTimeHelper.todayIntervalOrigin(Interval.Quarter)
  const yearOrigin = dateTimeHelper.todayIntervalOrigin(Interval.Year)

  const {
    dateRangeState: { timeRange },
    dateRangeDispatch,
    onTimeRange,
    intervals,
  } = useDateRangeContext()

  const onPresetSelect = React.useCallback(
    (origin: IntervalOrigin) => {
      dateRangeDispatch({ type: "SET_INTERVAL_ORIGIN", origin })
      const newRange = dateTimeHelper.periodFromIntervalOrigin(origin)
      onChange?.(newRange)
      onTimeRange(newRange)
    },
    [dateRangeDispatch, onChange, onTimeRange]
  )

  const {
    lastMonth,
    thisMonth,
    last3,
    last12,
    thisQuarter,
    lastQuarter,
    thisYear,
    lastYear,
    last10Years,
    yearToDate,
  } = React.useMemo(() => {
    const currentOrigin = dateTimeHelper.intervalOriginFromPeriodRange(timeRange)
    return {
      lastMonth: dateTimeHelper.addIntervalToOrigin(todayOrigin, -1),
      thisMonth: { ...todayOrigin, intervalCount: 1 },
      last3: { ...todayOrigin, intervalCount: 3 },
      last12: { ...todayOrigin, intervalCount: 12 },
      thisQuarter: { ...quarterOrigin, intervalCount: 1 },
      lastQuarter: dateTimeHelper.addIntervalToOrigin(quarterOrigin, -1),
      thisYear: yearOrigin,
      lastYear: dateTimeHelper.addIntervalToOrigin(yearOrigin, -1),
      last10Years: { ...yearOrigin, intervalCount: 10 },
      yearToDate: dateTimeHelper.yearToDateOrigin(currentOrigin),
    }
  }, [quarterOrigin, timeRange, todayOrigin, yearOrigin])

  const {
    isLastMonth,
    isThisMonth,
    isLast3,
    isLast12,
    isThisQuarter,
    isLastQuarter,
    isThisYear,
    isLastYear,
    isLast10Years,
    isYearToDate,
  } = React.useMemo(() => {
    const currentOrigin = dateTimeHelper.intervalOriginFromPeriodRange(timeRange)
    return {
      isLastMonth: dateTimeHelper.areIntervalOriginsEqual(lastMonth, currentOrigin),
      isThisMonth: dateTimeHelper.areIntervalOriginsEqual(thisMonth, currentOrigin),
      isLast3: dateTimeHelper.areIntervalOriginsEqual(last3, currentOrigin),
      isLast12: dateTimeHelper.areIntervalOriginsEqual(last12, currentOrigin),
      isThisQuarter: dateTimeHelper.areIntervalOriginsEqual(thisQuarter, currentOrigin),
      isLastQuarter: dateTimeHelper.areIntervalOriginsEqual(lastQuarter, currentOrigin),
      isThisYear: dateTimeHelper.areIntervalOriginsEqual(thisYear, currentOrigin),
      isLastYear: dateTimeHelper.areIntervalOriginsEqual(lastYear, currentOrigin),
      isLast10Years: dateTimeHelper.areIntervalOriginsEqual(last10Years, currentOrigin),
      isYearToDate: dateTimeHelper.areIntervalOriginsEqual(yearToDate, currentOrigin),
    }
  }, [
    last10Years,
    last12,
    last3,
    lastMonth,
    lastQuarter,
    lastYear,
    thisMonth,
    thisQuarter,
    thisYear,
    timeRange,
    yearToDate,
  ])

  const shouldShowYTD = React.useMemo(() => {
    if (intervals[Interval.Month] !== "range") return false
    const currentMonth = new Date().getMonth()
    return currentMonth !== 0 // Hide in Jan (month index 0)
  }, [intervals])

  const shouldShowLast3 = React.useMemo(() => {
    if (intervals[Interval.Month] !== "range") return false
    const currentMonth = new Date().getMonth()
    return currentMonth !== 2 // Hide in March (month index 2)
  }, [intervals])

  const shouldShowLast12 = React.useMemo(() => {
    if (intervals[Interval.Month] !== "range") return false
    const currentMonth = new Date().getMonth()
    return currentMonth !== 11 // Hide in December (month index 11)
  }, [intervals])

  const shouldShowLast10Yrs = React.useMemo(() => intervals[Interval.Year] === "range", [intervals])

  return (
    <PresetsContainer>
      <Presets>
        <Preset origin={thisMonth} active={isThisMonth} onSelect={onPresetSelect}>
          This Month
        </Preset>
        <Preset origin={lastMonth} active={isLastMonth} onSelect={onPresetSelect}>
          Last Month
        </Preset>
        {shouldShowLast3 && (
          <Preset origin={last3} active={isLast3} onSelect={onPresetSelect}>
            Last 3 Months
          </Preset>
        )}
        {shouldShowLast12 && (
          <Preset origin={last12} active={isLast12} onSelect={onPresetSelect}>
            Last 12 Months
          </Preset>
        )}

        {intervals[Interval.Quarter] && (
          <>
            <Divider />
            <Preset origin={thisQuarter} active={isThisQuarter} onSelect={onPresetSelect}>
              This Quarter
            </Preset>
            <Preset origin={lastQuarter} active={isLastQuarter} onSelect={onPresetSelect}>
              Last Quarter
            </Preset>
          </>
        )}

        <Divider />
        <Preset origin={thisYear} active={isThisYear} onSelect={onPresetSelect}>
          This Year
        </Preset>
        <Preset origin={lastYear} active={isLastYear} onSelect={onPresetSelect}>
          Last Year
        </Preset>
        {shouldShowYTD && (
          <Preset origin={yearToDate} active={isYearToDate} onSelect={onPresetSelect}>
            Year to Date
          </Preset>
        )}
        {shouldShowLast10Yrs && (
          <Preset origin={last10Years} active={isLast10Years} onSelect={onPresetSelect}>
            Last 10 Years
          </Preset>
        )}

        <Divider />
        <PresetButton onClick={() => dateRangeDispatch({ type: "TOGGLE_CUSTOM", custom: true })}>
          <div>Custom</div>
          <SvgChevronRight />
        </PresetButton>
      </Presets>
    </PresetsContainer>
  )
}

const Preset: React.FC<PresetProps> = ({ origin, active, onSelect, children }) => (
  <PresetButton
    className={stringHelper.classNames(active && "active")}
    onClick={() => onSelect(origin)}
  >
    {children}
  </PresetButton>
)
